import { useEffect, useState } from "react";
import {
  type AfirmacionDocumento,
  type ClienteNexo,
  type DecisionRevision,
  type DocumentoDetalle,
  type DocumentoResumen,
  type NodoRedDocumento,
  type RedDocumento,
  justiciaRevisarAfirmacion,
  justiciaRevisarResolucion,
  vizDocumento,
  vizDocumentoRed,
  vizDocumentos,
} from "../api.ts";
import { C, CLAIM_STATUS_ES, MENCION_TIPO_ES, autoridadEs, fecha, st } from "../theme.ts";
import { BotonesRevision, Cargando, ErrorBox, celdaTd, celdaTh } from "../ui/componentes.tsx";
import { GrafoDocumento } from "../ui/GrafoDocumento.tsx";
import { rutaAHash } from "../rutas.ts";

const TIPO_ES: Record<string, string> = {
  alert: "Alert",
  advisory: "Advisory",
  notice: "Notice",
  special_measure: "Special measure",
  designation: "Sanción",
  press_release: "Press release",
  indictment: "Indictment",
  complaint: "Complaint",
  chart: "Organigrama OFAC",
  image: "Imagen",
  otro: "Documento",
};

const REL_ES: Record<string, string> = {
  suplementa: "suplementa a",
  coordina_con: "coordinada con",
  rescinde: "rescinde a",
  actualiza: "actualiza a",
  referencia: "cita a",
  adjunto: "adjunta",
};

/**
 * Documentos — la capa de inteligencia documental FinCEN/OFAC (§3.5): alerts
 * con red flags numerados y SAR key terms, órdenes de special measures y las
 * designaciones OFAC coordinadas, con su cadena documental.
 */
export function Documentos({ cliente, id }: { cliente: ClienteNexo; id?: string }) {
  if (id) return <DetalleDocumento cliente={cliente} id={id} />;
  return <ListaDocumentos cliente={cliente} />;
}

type Categoria = "corpus" | "fincen_ofac" | "justicia" | "todos";

function categoriaDe(d: DocumentoResumen): Exclude<Categoria, "todos"> {
  if (d.fuente_id === "nexo_corpus_documental") return "corpus";
  if (d.fuente_id === "us_ofac_sdn" || d.fuente_id === "us_fincen" || d.fuente_id === "us_treasury_ofac") {
    return "fincen_ofac";
  }
  return "justicia";
}

const CATEGORIAS: { valor: Categoria; etiqueta: string }[] = [
  { valor: "corpus", etiqueta: "Corpus extraído" },
  { valor: "fincen_ofac", etiqueta: "Oficina del Tesoro contra el lavado / OFAC" },
  { valor: "justicia", etiqueta: "FBI / DOJ (screening)" },
  { valor: "todos", etiqueta: "Todos" },
];

function ListaDocumentos({ cliente }: { cliente: ClienteNexo }) {
  const [docs, setDocs] = useState<DocumentoResumen[] | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [categoria, setCategoria] = useState<Categoria>("corpus");
  const [q, setQ] = useState("");

  useEffect(() => {
    let vivo = true;
    vizDocumentos(cliente)
      .then((d) => {
        if (vivo) setDocs(d);
      })
      .catch((e: unknown) => {
        if (vivo) setError(e instanceof Error ? e.message : String(e));
      });
    return () => {
      vivo = false;
    };
  }, [cliente]);

  if (error) return <ErrorBox mensaje={error} />;
  if (docs === undefined) return <Cargando texto="Cargando documentos…" />;

  // Conteo por categoría (sobre el total), para las pestañas.
  const conteos: Record<Categoria, number> = { corpus: 0, fincen_ofac: 0, justicia: 0, todos: docs.length };
  for (const d of docs) conteos[categoriaDe(d)] += 1;

  const qn = q.trim().toLowerCase();
  const filtrados = docs.filter((d) =>
    (categoria === "todos" || categoriaDe(d) === categoria) &&
    (qn === "" || d.titulo.toLowerCase().includes(qn) ||
      (d.codigos ?? []).some((c) => c.toLowerCase().includes(qn)))
  );

  return (
    <div>
      <div style={{ ...st.tarjeta }}>
        <h1 style={{ ...st.display, fontSize: 22, margin: "0 0 4px" }}>Documentos</h1>
        <p style={{ fontSize: 13, color: C.muted, margin: 0, lineHeight: 1.5 }}>
          El <strong>corpus extraído</strong> (acusaciones, comunicados y perfiles procesados por el
          extractor documental, con sus nombres y afirmaciones), las <strong>sanciones de OFAC y
          alertas de la Oficina del Tesoro contra el lavado</strong> (con red flags y entidades), y el catálogo de <strong>FBI/DOJ</strong>
          {" "}usado para screening (sin extracción). Filtra por categoría para no mezclarlos.
        </p>
      </div>

      <div style={{ ...st.tarjeta }}>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 12 }}>
          {CATEGORIAS.map((c) => {
            const activa = categoria === c.valor;
            return (
              <button
                key={c.valor}
                type="button"
                onClick={() => { setCategoria(c.valor); }}
                style={{
                  ...st.btn,
                  fontWeight: activa ? 700 : 500,
                  color: activa ? "#fff" : C.ink,
                  background: activa ? C.tiel : "transparent",
                  borderColor: activa ? C.tiel : C.line,
                }}
              >
                {c.etiqueta} ({String(conteos[c.valor])})
              </button>
            );
          })}
          <input
            type="search"
            value={q}
            onChange={(e) => { setQ(e.target.value); }}
            placeholder="Buscar por título…"
            style={{ ...st.input, flex: 1, minWidth: 180 }}
          />
        </div>
        <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px" }}>
          Mostrando {String(filtrados.length)} de {String(docs.length)} documentos.
        </p>
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead>
            <tr>
              <th style={celdaTh}>Fecha</th>
              <th style={celdaTh}>Tipo</th>
              <th style={celdaTh}>Documento</th>
              <th style={{ ...celdaTh, textAlign: "center" }}>Nombres</th>
              <th style={{ ...celdaTh, textAlign: "center" }}>Afirmaciones</th>
              <th style={{ ...celdaTh, textAlign: "center" }}>Red flags</th>
            </tr>
          </thead>
          <tbody>
            {filtrados.map((d) => {
              const menciones = d.menciones ?? 0;
              const afirmaciones = d.afirmaciones ?? 0;
              const sinContenido = menciones === 0 && afirmaciones === 0 && d.red_flags === 0 && d.entidades === 0;
              return (
                <tr key={d.id}>
                  <td style={{ ...celdaTd, whiteSpace: "nowrap", color: C.muted, fontSize: 12.5 }}>{fecha(d.fecha)}</td>
                  <td style={{ ...celdaTd, whiteSpace: "nowrap", fontSize: 12 }}>
                    <span style={{ fontWeight: 700 }}>{autoridadEs(d.autoridad)}</span>
                    <span style={{ color: C.muted }}> · {TIPO_ES[d.tipo] ?? d.tipo}</span>
                  </td>
                  <td style={celdaTd}>
                    <a
                      href={rutaAHash({ vista: "documento", id: d.id })}
                      style={{ color: C.tiel, textDecoration: "none", fontWeight: 600, fontSize: 13 }}
                    >
                      {d.titulo}
                    </a>
                    {d.codigos && d.codigos.length > 0 ? (
                      <span style={{ marginLeft: 6 }}>
                        {d.codigos.map((c) => (
                          <span
                            key={c}
                            style={{
                              fontFamily: "ui-monospace, Consolas, monospace",
                              fontSize: 10.5,
                              fontWeight: 700,
                              color: C.tielDark,
                              background: C.tielSoft,
                              borderRadius: 4,
                              padding: "1px 5px",
                              marginRight: 3,
                            }}
                          >
                            {c}
                          </span>
                        ))}
                      </span>
                    ) : null}
                    <div style={{ color: C.muted, fontSize: 11.5 }}>
                      {d.esquema ? `${d.esquema} · ` : ""}
                      {d.sar_key_term ? (
                        <span style={{ fontFamily: "ui-monospace, Consolas, monospace" }}>{d.sar_key_term}</span>
                      ) : null}
                      {sinContenido ? (
                        <span title="Solo se capturó la ficha/portada de la fuente, no el texto completo (p. ej. libros por derechos de autor); no hay nada que extraer.">
                          solo referencia — sin texto extraíble
                        </span>
                      ) : null}
                    </div>
                  </td>
                  <td style={{ ...celdaTd, textAlign: "center", fontVariantNumeric: "tabular-nums" }}>
                    {menciones > 0 ? String(menciones) : "—"}
                  </td>
                  <td style={{ ...celdaTd, textAlign: "center", fontVariantNumeric: "tabular-nums", fontWeight: afirmaciones > 0 ? 700 : 400, color: afirmaciones > 0 ? C.ink : C.muted }}>
                    {afirmaciones > 0 ? String(afirmaciones) : "—"}
                  </td>
                  <td style={{ ...celdaTd, textAlign: "center" }}>{d.red_flags > 0 ? String(d.red_flags) : "—"}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function DetalleDocumento({ cliente, id }: { cliente: ClienteNexo; id: string }) {
  const [doc, setDoc] = useState<DocumentoDetalle | null | undefined>(undefined);
  const [error, setError] = useState<string | null>(null);
  const [red, setRed] = useState<RedDocumento | null>(null);

  useEffect(() => {
    let vivo = true;
    setDoc(undefined);
    setRed(null);
    vizDocumento(cliente, id)
      .then((d) => {
        if (vivo) setDoc(d);
      })
      .catch((e: unknown) => {
        if (vivo) setError(e instanceof Error ? e.message : String(e));
      });
    // La red de afirmaciones extraídas es opcional: documentos sin extracción
    // (o un despliegue sin la migración 0097) simplemente no muestran la
    // sección.
    vizDocumentoRed(cliente, id)
      .then((r) => {
        if (vivo) setRed(r && r.aristas.length > 0 ? r : null);
      })
      .catch(() => undefined);
    return () => {
      vivo = false;
    };
  }, [cliente, id]);

  const [errorRevision, setErrorRevision] = useState<string | null>(null);

  async function revisarResolucion(resolucionId: number, decision: DecisionRevision): Promise<void> {
    setErrorRevision(null);
    try {
      await justiciaRevisarResolucion(cliente, resolucionId, decision);
      setRed((r) => r
        ? {
          ...r,
          nodos: r.nodos.map((n) =>
            n.resolucion_id === resolucionId
              ? (decision === "approved"
                ? { ...n, resolucion_estado: "approved" as const }
                : { ...n, resolucion_id: null, resolucion_estado: null, resolucion_score: null, entidad_id: null, entidad_caption: null, entidad_schema: null })
              : n
          ),
        }
        : r);
    } catch (e) {
      setErrorRevision(e instanceof Error ? e.message : String(e));
    }
  }

  async function revisarAfirmacion(afirmacionId: string, decision: DecisionRevision): Promise<void> {
    setErrorRevision(null);
    try {
      await justiciaRevisarAfirmacion(cliente, afirmacionId, decision);
      setRed((r) => r
        ? {
          ...r,
          aristas: decision === "rejected"
            ? r.aristas.filter((a) => a.id !== afirmacionId)
            : r.aristas.map((a) => (a.id === afirmacionId ? { ...a, estado: "approved" as const } : a)),
          afirmaciones: decision === "rejected"
            ? r.afirmaciones?.filter((a) => a.id !== afirmacionId)
            : r.afirmaciones?.map((a) => (a.id === afirmacionId ? { ...a, estado: "approved" as const } : a)),
        }
        : r);
    } catch (e) {
      setErrorRevision(e instanceof Error ? e.message : String(e));
    }
  }

  if (error) return <ErrorBox mensaje={error} />;
  if (doc === undefined) return <Cargando texto="Cargando el documento…" />;
  if (doc === null) return <ErrorBox mensaje="No existe un documento con ese identificador." />;

  const d = doc.documento;

  return (
    <div>
      <a
        href={rutaAHash({ vista: "documentos" })}
        style={{ fontSize: 13, color: C.tiel, textDecoration: "none", fontWeight: 600 }}
      >
        ← Documentos
      </a>

      <div style={{ ...st.tarjeta, marginTop: 10 }}>
        <div style={{ display: "flex", justifyContent: "space-between", gap: 12, alignItems: "flex-start" }}>
          <p style={st.eyebrow}>
            {autoridadEs(d.autoridad)} · {TIPO_ES[d.tipo] ?? d.tipo} · {fecha(d.fecha)}
          </p>
          <a
            href={rutaAHash({ vista: "revisar", id: d.id })}
            style={{ ...st.btnPrim, textDecoration: "none", flex: "none", whiteSpace: "nowrap" }}
          >
            Revisar texto completo
          </a>
        </div>
        <h1 style={{ ...st.display, fontSize: 22, margin: "0 0 8px" }}>{d.titulo}</h1>
        <div style={{ display: "flex", gap: 14, flexWrap: "wrap", alignItems: "center", marginBottom: 8 }}>
          {d.sar_key_term ? (
            <span
              style={{
                fontFamily: "ui-monospace, Consolas, monospace",
                fontSize: 12.5,
                background: "rgba(0,0,0,0.05)",
                padding: "3px 8px",
                borderRadius: 6,
              }}
              title="Key term que la Oficina del Tesoro contra el lavado pide citar en el SAR"
            >
              {d.sar_key_term}
            </span>
          ) : null}
          {d.esquema ? <span style={{ fontSize: 12.5, color: C.muted }}>Esquema: {d.esquema}</span> : null}
          {d.url ? (
            <a href={d.url} target="_blank" rel="noreferrer" style={{ fontSize: 12.5, color: C.tiel }}>
              Documento original ↗
            </a>
          ) : null}
        </div>
        {d.resumen ? (
          <p style={{ fontSize: 13.5, color: C.ink, margin: 0, lineHeight: 1.55 }}>{d.resumen}</p>
        ) : null}
        {doc.relacionados.length > 0 ? (
          <p style={{ fontSize: 12.5, color: C.muted, margin: "10px 0 0" }}>
            {doc.relacionados.map((r, i) => (
              <span key={i}>
                {i > 0 ? " · " : ""}
                {r.dir === "sale" ? REL_ES[r.tipo] ?? r.tipo : `(${REL_ES[r.tipo] ?? r.tipo} por)`}{" "}
                <a href={rutaAHash({ vista: "documento", id: r.documento })} style={{ color: C.tiel }}>
                  {r.documento}
                </a>
              </span>
            ))}
          </p>
        ) : null}
      </div>

      {doc.entidades.length > 0 ? (
        <div style={{ ...st.tarjeta }}>
          <p style={st.eyebrow}>Entidades ({String(doc.entidades.length)})</p>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <tbody>
              {doc.entidades.map((en) => (
                <tr key={en.entidad_id}>
                  <td style={{ ...celdaTd, fontWeight: 600 }}>
                    <a
                      href={rutaAHash({ vista: "entidad", id: en.entidad_id })}
                      style={{ color: C.tiel, textDecoration: "none" }}
                    >
                      {en.caption ?? en.entidad_id}
                    </a>
                  </td>
                  <td style={{ ...celdaTd, color: C.muted, fontSize: 12.5 }}>{en.pais ?? ""}</td>
                  <td style={{ ...celdaTd, whiteSpace: "nowrap", fontSize: 11.5, color: en.rol === "designado" ? C.red : C.muted, fontWeight: en.rol === "designado" ? 700 : 400 }}>
                    {en.rol === "sujeto_medida" ? "objeto de la medida" : en.rol === "designado" ? "sancionado" : en.rol}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      {red ? (
        <div style={{ ...st.tarjeta }}>
          <p style={st.eyebrow}>
            Red de afirmaciones extraídas ({String(red.nodos.length)} nombres · {String(red.aristas.length)} afirmaciones)
          </p>
          <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px", lineHeight: 1.5 }}>
            Personas, empresas y objetos tal como aparecen en el documento, extraídos por el pipeline
            documental con evidencia literal. Línea punteada = afirmación propuesta, pendiente de revisión
            humana; los nodos con anillo tienen candidato en el grafo Nexo y el clic abre su ficha. Nada de
            esto es todavía una relación confirmada.
          </p>
          <GrafoDocumento red={red} />
        </div>
      ) : null}

      {errorRevision ? (
        <ErrorBox mensaje={`No se pudo registrar la revisión: ${errorRevision}`} />
      ) : null}
      {red ? <TablaNombresExtraidos nodos={red.nodos} onRevisar={revisarResolucion} /> : null}
      {red?.afirmaciones && red.afirmaciones.length > 0 ? (
        <TablaAfirmaciones afirmaciones={red.afirmaciones} onRevisar={revisarAfirmacion} />
      ) : null}

      {doc.red_flags.length > 0 ? (
        <div style={{ ...st.tarjeta }}>
          <p style={st.eyebrow}>Red flags ({String(doc.red_flags.length)})</p>
          <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px" }}>
            Texto original de la Oficina del Tesoro contra el lavado. Ningún red flag es determinante por
            sí solo: son insumos de monitoreo, no acusaciones.
          </p>
          <ol style={{ margin: 0, paddingLeft: 22, fontSize: 13, lineHeight: 1.6, color: C.ink }}>
            {doc.red_flags.map((rf) => (
              <li key={rf.numero ?? rf.texto} style={{ marginBottom: 8 }}>
                {rf.texto}
                {rf.tema ? (
                  <span style={{ color: C.muted, fontSize: 11.5, fontWeight: 700 }}> · {rf.tema}</span>
                ) : null}
              </li>
            ))}
          </ol>
        </div>
      ) : null}
    </div>
  );
}

/** Los nombres del grafo, ordenados por peso, con tipo y candidato canónico. */
function TablaNombresExtraidos({
  nodos,
  onRevisar,
}: {
  nodos: NodoRedDocumento[];
  onRevisar: (resolucionId: number, decision: DecisionRevision) => Promise<void>;
}) {
  const [verTodos, setVerTodos] = useState(false);
  if (nodos.length === 0) return null;
  const visibles = verTodos ? nodos : nodos.slice(0, 25);
  return (
    <div style={{ ...st.tarjeta }}>
      <p style={st.eyebrow}>Nombres extraídos ({String(nodos.length)})</p>
      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px", lineHeight: 1.5 }}>
        Aprobar un candidato confirma que el nombre del documento se refiere a esa entidad del grafo
        Nexo; rechazarlo lo descarta. La decisión queda registrada con tu usuario.
      </p>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={celdaTh}>Nombre en el documento</th>
            <th style={celdaTh}>Tipo</th>
            <th style={{ ...celdaTh, textAlign: "center" }}>Afirmaciones</th>
            <th style={celdaTh}>Candidato en Nexo</th>
            <th style={celdaTh}>Revisión</th>
          </tr>
        </thead>
        <tbody>
          {visibles.map((n) => (
            <tr key={n.norm}>
              <td style={{ ...celdaTd, fontWeight: 600, fontSize: 13 }}>{n.texto}</td>
              <td style={{ ...celdaTd, fontSize: 12, color: C.muted }}>
                {n.tipo ? MENCION_TIPO_ES[n.tipo] ?? n.tipo : "—"}
              </td>
              <td style={{ ...celdaTd, textAlign: "center" }}>{String(n.n_afirmaciones)}</td>
              <td style={{ ...celdaTd, fontSize: 12.5 }}>
                {n.entidad_id ? (
                  <>
                    <a
                      href={rutaAHash({ vista: "entidad", id: n.entidad_id })}
                      style={{ color: C.tiel, textDecoration: "none", fontWeight: 600 }}
                    >
                      {n.entidad_caption ?? n.entidad_id}
                    </a>
                    <span style={{ color: n.resolucion_estado === "approved" ? C.green : C.amber, fontSize: 11.5, fontWeight: 700 }}>
                      {n.resolucion_estado === "approved" ? " ✓ aprobado" : " · propuesto"}
                      {n.resolucion_score !== null ? ` (${String(n.resolucion_score)})` : ""}
                    </span>
                  </>
                ) : (
                  <span style={{ color: C.muted }}>sin match</span>
                )}
              </td>
              <td style={celdaTd}>
                {n.resolucion_id !== null && n.resolucion_estado === "proposed" ? (
                  <BotonesRevision
                    aprobar="approved"
                    rechazar="rejected"
                    onDecidir={(d) => onRevisar(n.resolucion_id ?? 0, d)}
                  />
                ) : n.resolucion_estado === "approved" ? (
                  <span style={{ fontSize: 11.5, color: C.green, fontWeight: 700 }}>revisado</span>
                ) : null}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {nodos.length > 25 ? (
        <button type="button" style={{ ...st.btn, marginTop: 8 }} onClick={() => { setVerTodos(!verTodos); }}>
          {verTodos ? "Mostrar solo los 25 principales" : `Mostrar los ${String(nodos.length)} nombres`}
        </button>
      ) : null}
    </div>
  );
}

/** Todas las afirmaciones extraídas del documento, con evidencia literal. */
function TablaAfirmaciones({
  afirmaciones,
  onRevisar,
}: {
  afirmaciones: AfirmacionDocumento[];
  onRevisar: (afirmacionId: string, decision: DecisionRevision) => Promise<void>;
}) {
  const [verTodas, setVerTodas] = useState(false);
  const visibles = verTodas ? afirmaciones : afirmaciones.slice(0, 30);
  return (
    <div style={{ ...st.tarjeta }}>
      <p style={st.eyebrow}>Afirmaciones extraídas ({String(afirmaciones.length)})</p>
      <p style={{ fontSize: 12, color: C.muted, margin: "0 0 10px", lineHeight: 1.5 }}>
        Cada fila es una relación propuesta por el extractor con su cita textual del documento y el
        estado probatorio que el propio texto soporta. Nada se presenta como hecho probado: la
        revisión humana vive en la pestaña Justicia.
      </p>
      <table style={{ width: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            <th style={celdaTh}>Afirmación y evidencia</th>
            <th style={celdaTh}>Estado probatorio</th>
            <th style={{ ...celdaTh, textAlign: "center" }}>Pág.</th>
            <th style={celdaTh}>Revisión</th>
          </tr>
        </thead>
        <tbody>
          {visibles.map((a) => (
            <tr key={a.id}>
              <td style={celdaTd}>
                <div style={{ fontWeight: 600, fontSize: 12.5 }}>
                  {a.subject_text}
                  <span style={{ color: C.tiel }}> {a.predicate.replace(/_/g, " ")} </span>
                  {a.object_text}
                </div>
                <div style={{ color: C.muted, fontSize: 11.5, marginTop: 2, lineHeight: 1.45 }}>
                  “{a.evidence_excerpt}”
                </div>
              </td>
              <td style={{ ...celdaTd, whiteSpace: "nowrap", fontSize: 12, color: C.muted }}>
                {a.claim_status ? CLAIM_STATUS_ES[a.claim_status] ?? a.claim_status : "—"}
              </td>
              <td style={{ ...celdaTd, textAlign: "center", fontSize: 12, color: C.muted }}>
                {a.page !== null ? String(a.page) : "—"}
              </td>
              <td style={{ ...celdaTd, whiteSpace: "nowrap", fontSize: 11.5 }}>
                {a.estado === "approved" ? (
                  <span style={{ color: C.green, fontWeight: 700 }}>aprobada</span>
                ) : (
                  <BotonesRevision aprobar="approved" rechazar="rejected" onDecidir={(d) => onRevisar(a.id, d)} />
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      {afirmaciones.length > 30 ? (
        <button type="button" style={{ ...st.btn, marginTop: 8 }} onClick={() => { setVerTodas(!verTodas); }}>
          {verTodas ? "Mostrar solo las 30 primeras" : `Mostrar las ${String(afirmaciones.length)} afirmaciones`}
        </button>
      ) : null}
    </div>
  );
}
